import React, { useRef } from 'react';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import {
  ExpandableButton,
  type ButtonProps,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import IconButton from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row');

type Props = {
  buttonLabel: string;
  title: string;
  width?: string;
  children: React.ReactNode;
  disabled?: boolean;
  disabledTooltip?: string | null;
  icon?: ButtonProps['icon'];
  iconPosition?: ButtonProps['iconPosition'];
  contentStyles?: React.CSSProperties;
};

const PopoverButton = ({
  buttonLabel,
  title,
  width = '330px',
  children,
  disabled = false,
  disabledTooltip,
  icon,
  iconPosition,
  contentStyles,
}: Props) => {
  const anchorElRef = useRef(null);
  const [isOpen, open, close] = useModal();

  const boxStyle = {
    border: `1px solid ${FullPalette.GREY_600}`,
    borderRadius: '5px',
    boxShadow: `0px 3px 11px 0px ${FullPalette.EVERGREEN_E200}`,
    bgcolor: 'background.paper',
    width: `${width}`,
    maxHeight: 'calc(100vh - 240px)',
    overflow: 'visible',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  };
  const contentWrapperStyle = {
    flexGrow: 1,
    minHeight: 0,
    padding: '16px 24px 24px 24px',
    overflowY: 'auto',
    overflowX: 'hidden',
    ...contentStyles,
  };

  const ButtonEl = (
    <ExpandableButton
      size="md"
      onClick={open}
      disabled={disabled}
      icon={icon}
      iconPosition={iconPosition}
    >
      {buttonLabel}
    </ExpandableButton>
  );

  return (
    <div ref={anchorElRef}>
      <Tooltip ariaLabel={disabledTooltip || ''} label={disabledTooltip || ''}>
        {ButtonEl}
      </Tooltip>
      {anchorElRef?.current && (
        <Popover
          open={isOpen}
          onClose={close}
          anchorEl={anchorElRef.current}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          disableScrollLock={false}
          slotProps={{
            paper: {
              sx: {
                overflow: 'visible',
                position: 'relative',
                '& .MuiPaper-root': {
                  overflow: 'visible',
                },
              },
            },
          }}
          sx={{
            zIndex: 150,
            '& .MuiPopover-paper': {
              overflow: 'visible',
            },
          }}
        >
          <Box sx={boxStyle}>
            <Box sx={{ padding: '24px 24px 0' }}>
              <Stack direction="row" justifyContent="space-between">
                <Body medium>{title}</Body>
                <IconButton
                  label={t('close')}
                  onClick={close}
                  icon={CloseIcon}
                  size="md"
                  flat
                />
              </Stack>
            </Box>
            <Box sx={contentWrapperStyle}>{children}</Box>
          </Box>
        </Popover>
      )}
    </div>
  );
};

export default PopoverButton;
